import useQuery from '@/api/queries/useQuery';
import { UseQueryResult } from '@tanstack/react-query';

import { usePartnerScoreGlobalFilter } from '@/components/partner';
import { usePartnerScoreFilter, UsePartnerScoreFilterReturn } from '@/pages/PartnerScore/components/PartnerScoreFilter';
import { CubeQuery } from '@/types/cube';
import { toNumberOrNull } from '@/utils/cube';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { AxiosError } from 'axios';
import { PartnerScoreCacheKeys } from './cacheKeys';
import { extractCurrentAndPrevious, getIsValidCurrentPeriod, sortByYearQuarter } from './utils';
import { sumAverageForPeriod } from './utils/sumAverageForPeriod';

const CUBE_NAME = 'partnerscore';

type UseGetAveragePartnerScoreResponse = {
  currentAverage?: number;
  change?: number;
};

interface UseGetAveragePartnerScoreParams extends Partial<Pick<UsePartnerScoreFilterReturn, 'year' | 'quarter'>> {
  surveyIdList?: UsePartnerScoreFilterReturn['surveyIdList'];
  hcpCountryCode?: string;
}

function useGetAveragePartnerScore({
  year,
  quarter,
  surveyIdList = [],
  hcpCountryCode,
}: UseGetAveragePartnerScoreParams): UseQueryResult<UseGetAveragePartnerScoreResponse, AxiosError> {
  const { globalFiltersWithoutHcpCountryCode, countries } = usePartnerScoreGlobalFilter();
  const { filtersWithImmediatePreviousPeriods, filterQuarterDimensions, filterSurveyList } = usePartnerScoreFilter();

  const selectedYear = year ?? 0;
  const selectedQuarter = quarter ?? 0;

  const periodFilters =
    filtersWithImmediatePreviousPeriods?.({
      cubeNameArg: CUBE_NAME,
      currentYear: selectedYear,
      currentQuarter: selectedQuarter,
    }) ?? [];

  const baseDimensions = [`${CUBE_NAME}.quarter`, `${CUBE_NAME}.year`, `${CUBE_NAME}.country_code`];

  const query: CubeQuery = {
    measures: [`${CUBE_NAME}.sumPartnerScore`, `${CUBE_NAME}.countOfSurveys`],
    dimensions: filterQuarterDimensions?.(baseDimensions, selectedQuarter) ?? baseDimensions,
    order: { [`${CUBE_NAME}.year`]: 'desc', [`${CUBE_NAME}.quarter`]: 'desc' },
    filters: [
      {
        member: `${CUBE_NAME}.country_code`,
        operator: 'equals',
        values: hcpCountryCode ? [hcpCountryCode] : countries,
      },
      {
        member: `${CUBE_NAME}.trans_q3_partnerindexscore`,
        operator: 'set',
      },
      globalFiltersWithoutHcpCountryCode,
      ...(filterSurveyList?.(CUBE_NAME, surveyIdList ?? []) ?? []),
      ...periodFilters,
    ],
    limit: 100,
  };

  const isEnabled = (!!countries.length || !!hcpCountryCode) && !!year && !!quarter;

  return useQuery<CubeRawRow[], AxiosError, UseGetAveragePartnerScoreResponse>({
    payload: query,
    options: {
      queryKey: [...PartnerScoreCacheKeys.averagePartnerScore(query)],
      select: (data): UseGetAveragePartnerScoreResponse => {
        if (!data.length) return {};

        const isValidCurrent = getIsValidCurrentPeriod({
          cubeName: CUBE_NAME,
          data,
          year: selectedYear,
          quarter: selectedQuarter,
        });

        if (!isValidCurrent) return {};

        const sortedData = sortByYearQuarter(
          data.map(record => ({
            year: toNumberOrNull(record[`${CUBE_NAME}.year`]) ?? 0,
            quarter: toNumberOrNull(record[`${CUBE_NAME}.quarter`]) ?? 0,
            sumPartnerScore: toNumberOrNull(record[`${CUBE_NAME}.sumPartnerScore`]) ?? 0,
            countOfSurveys: toNumberOrNull(record[`${CUBE_NAME}.countOfSurveys`]) ?? 0,
          })),
          selectedYear,
          selectedQuarter,
        );

        const aggregatedData = sumAverageForPeriod({
          array: sortedData,
          keys: ['year', 'quarter', 'sumPartnerScore', 'countOfSurveys'],
        });

        const { currentAverage, change } = extractCurrentAndPrevious(
          aggregatedData,
          'sumPartnerScore',
          'countOfSurveys',
        );

        return {
          currentAverage,
          change,
        };
      },
      enabled: isEnabled,
    },
  });
}

export default useGetAveragePartnerScore;
